import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { GraduationCap, BookOpen, MessageCircle, Users, Trophy, Plus, LogOut, User, Settings, BarChart3 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const API = `${window.location.origin}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState({ courses: 0, completed: 0, inProgress: 0 });
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(false);
  
  useEffect(() => {
    // Load all data in parallel
    loadAllData();
    
    // Check if just subscribed (from URL parameter)
    if (searchParams.get('subscribed') === 'true') {
      setShowSubscriptionBanner(true);
      searchParams.delete('subscribed');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadAllData = async () => {
    try {
      // Parallelize ALL API calls
      const [progressRes, coursesRes, subscriptionRes, authRes] = await Promise.all([
        axios.get(`${API}/progress`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/courses`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/subscription/check`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);

      // Process progress stats
      const progress = progressRes.data || [];
      const completed = progress.filter(p => p.completion_percentage >= 100).length;
      const inProgress = progress.filter(p => p.completion_percentage > 0 && p.completion_percentage < 100).length;

      setStats({
        courses: (coursesRes.data || []).length,
        completed,
        inProgress
      });

      // Process subscription
      if (subscriptionRes.data) {
        setSubscription(subscriptionRes.data);
      }
      setLoadingSubscription(false);

      // Process first visit
      if (authRes.data) {
        const firstVisit = authRes.data.first_visit;
        setIsFirstVisit(firstVisit);
        
        // Mark visited in background (don't await)
        if (firstVisit) {
          axios.post(`${API}/auth/mark-visited`, {}, { withCredentials: true }).catch(() => {});
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoadingSubscription(false);
    }
  };

  const quickActions = [
    {
      title: 'Browse Courses',
      description: 'Explore infinite AI-generated custom history courses',
      icon: BookOpen,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
      borderColor: 'border-amber-200',
      onClick: () => navigate('/courses'),
      testId: 'browse-courses-action'
    },
    {
      title: 'Debate Room',
      description: 'Build critical thinking and rhetorical skills using evidence',
      icon: Users,
      bgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      borderColor: 'border-yellow-200',
      onClick: () => navigate('/debate'),
      testId: 'debate-room-action'
    },
    {
      title: 'History Chat Room',
      description: 'Ask anything about history',
      icon: MessageCircle,
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      onClick: () => navigate('/chat'),
      testId: 'ai-chat-action'
    },
    {
      title: 'My Progress',
      description: 'Track your learning journey',
      icon: Trophy,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      borderColor: 'border-green-200',
      onClick: () => navigate('/progress'),
      testId: 'my-progress-action'
    }
  ];

  return (
    <div className="min-h-screen theme-bg">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b sticky top-0 z-50 shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <GraduationCap className={`w-8 h-8 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
              <span className={`text-2xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Lyceum</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Admin Usage Link - only visible for admin */}
              {user?.email === 'ta.jamieson6@gmail.com' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/admin/usage')}
                  className={`${isDarkMode ? 'text-amber-400 hover:text-amber-200 hover:bg-amber-900/50' : 'text-amber-700 hover:text-amber-900 hover:bg-amber-100'}`}
                  title="Admin: AI Usage Stats"
                >
                  <BarChart3 className="w-5 h-5" />
                </Button>
              )}
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center sm:bg-amber-100 sm:px-4 sm:py-2 rounded-full sm:hover:bg-amber-200 hover:shadow-md active:scale-95 transition-all cursor-pointer sm:border-2 border-transparent sm:hover:border-amber-300"
                data-testid="profile-link"
                title="Go to Profile & Settings"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className={`w-8 h-8 rounded-full ring-2 ${isDarkMode ? 'ring-amber-400' : 'ring-amber-200'} sm:ring-0`} />
                ) : (
                  <User className={`w-8 h-8 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'} p-1 rounded-full ring-2 ${isDarkMode ? 'ring-amber-400' : 'ring-amber-200'} sm:ring-0`} />
                )}
                <span className="text-amber-900 font-medium hidden sm:inline sm:ml-2">{user?.name}</span>
              </button>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className={`${isDarkMode ? 'text-amber-400 hover:text-amber-200 hover:bg-amber-900/50' : 'text-amber-700 hover:text-amber-900 hover:bg-amber-100'}`}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 page-enter">
        {/* Trial/Subscription Banner */}
        {!loadingSubscription && subscription?.has_subscription && subscription?.is_trial && subscription?.days_remaining > 0 && (
          <Card className="card-glow mb-8 bg-gradient-to-r from-blue-100 to-cyan-100 border-blue-300 shadow-xl" data-testid="trial-banner">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <div className="mb-4 sm:mb-0">
                  <h3 className="text-xl font-bold text-blue-900 mb-2">
                    Free Trial Active - {subscription.days_remaining} {subscription.days_remaining === 1 ? 'Day' : 'Days'} Remaining
                  </h3>
                  <p className="text-blue-800">Subscribe now to continue access after your trial ends</p>
                </div>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                  data-testid="subscribe-now-btn"
                >
                  Subscribe Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {!loadingSubscription && 
         showSubscriptionBanner && (
          <Card className="card-glow mb-8 bg-gradient-to-r from-green-100 to-emerald-100 border-green-300 shadow-xl relative" data-testid="active-subscription-banner">
            <CardContent className="p-6">
              <button
                onClick={() => {
                  setShowSubscriptionBanner(false);
                }}
                className="absolute top-2 right-2 text-green-700 hover:text-green-900 hover:bg-green-200 rounded-full p-1 transition-colors"
                aria-label="Dismiss banner"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <h3 className="text-xl font-bold text-green-900 mb-2">
                    ✓ Active Subscription
                  </h3>
                  <p className="text-green-800">You have full access to all features</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {!loadingSubscription && !subscription?.has_subscription && (
          <Card className="card-glow mb-8 bg-gradient-to-r from-red-100 to-orange-100 border-red-300 shadow-xl" data-testid="expired-banner">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <div className="mb-4 sm:mb-0">
                  <h3 className="text-xl font-bold text-red-900 mb-2">Trial Expired - Subscribe to Continue</h3>
                  <p className="text-red-800">Get unlimited access to all features for just $9.99/month</p>
                </div>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap"
                  data-testid="subscribe-required-btn"
                >
                  Subscribe Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Welcome Section */}
        <div className="mb-12 text-center sm:text-left">
          <h1 className={`text-4xl sm:text-5xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'} mb-4`} data-testid="dashboard-welcome-heading">
            {isFirstVisit ? 'Welcome' : 'Welcome back'}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
            {isFirstVisit ? 'Begin your journey through history with Professor Clio' : 'Continue your journey through history with Professor Clio'}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          <Card className="card-glow card-interactive border-amber-200 bg-white/90 backdrop-blur-sm shadow-lg text-center opacity-0 animate-slide-up animate-stagger-1" data-testid="stats-total-courses">
            <CardHeader className="pb-3">
              <CardDescription className="text-amber-700">Total Courses</CardDescription>
              <CardTitle className="text-4xl text-amber-900">{stats.courses}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-600">Available to explore</p>
            </CardContent>
          </Card>

          <Card className="card-glow card-interactive border-orange-200 bg-white/90 backdrop-blur-sm shadow-lg text-center opacity-0 animate-slide-up animate-stagger-2" data-testid="stats-in-progress">
            <CardHeader className="pb-3">
              <CardDescription className="text-orange-700">In Progress</CardDescription>
              <CardTitle className="text-4xl text-orange-900">{stats.inProgress}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-orange-600">Courses you're learning</p>
            </CardContent>
          </Card>

          <Card className="card-glow card-interactive border-green-200 bg-white/90 backdrop-blur-sm shadow-lg text-center opacity-0 animate-slide-up animate-stagger-3" data-testid="stats-completed">
            <CardHeader className="pb-3">
              <CardDescription className="text-green-700">Completed</CardDescription>
              <CardTitle className="text-4xl text-green-900">{stats.completed}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-green-600">Courses finished</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'} mb-6 text-center sm:text-left`}>Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Card
                  key={index}
                  className={`${action.borderColor} bg-white/90 backdrop-blur-sm cursor-pointer card-glow card-interactive shadow-lg text-center opacity-0 animate-slide-up animate-stagger-${index + 1}`}
                  onClick={action.onClick}
                  data-testid={action.testId}
                >
                  <CardHeader className="items-center">
                    <div className={`p-3 rounded-full ${action.bgColor} mb-2`}>
                      <Icon className={`w-10 h-10 ${action.iconColor}`} />
                    </div>
                    <CardTitle className="text-lg text-amber-900">{action.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-amber-700">{action.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Create New Course CTA */}
        <Card className="card-glow bg-gradient-to-r from-amber-100 to-orange-100 border-amber-300 shadow-xl text-center opacity-0 animate-slide-up animate-stagger-6">
          <CardHeader>
            <CardTitle className="text-2xl text-amber-900">Start a New Course</CardTitle>
            <CardDescription className="text-amber-800 text-base">
              Generate a custom AI-powered curriculum on any historical topic
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/courses')}
              className="bg-amber-600 hover:bg-amber-700 text-white btn-press pulse-subtle"
              size="lg"
              data-testid="start-new-course-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Course
            </Button>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;